<template>
  <div class="lead-status-changes-report">
    <div class="bg-white">
      <h1 class="text-2xl font-bold text-gray-900 mb-6">Lead Durumu Değişim Raporu</h1>

      <!-- Filters Section -->
      <div class="filters-container mb-6">
        <div class="flex gap-4 items-end">
          <div class="flex-1">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Tarih
            </label>
            <input
              v-model="selectedDate"
              type="date"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div class="flex-1">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Müşteri Temsilcisi
            </label>
            <select
              v-model="selectedAgent"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tümü</option>
              <option v-for="agent in agentList" :key="agent.id" :value="agent.id">
                {{ agent.username }}
              </option>
            </select>
          </div>
          <div>
            <button
              @click="loadReport"
              :disabled="loading"
              class="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="loading">Yükleniyor...</span>
              <span v-else>Raporu Getir</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Müşteri Temsilcisi Özeti Cards -->
      <div v-if="reportData && !loading && reportData.summary.changes_by_agent.length > 0" class="mb-6">
        <h2 class="text-base font-semibold text-gray-700 mb-3">Müşteri Temsilcisi Özeti</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div
            v-for="agentSummary in reportData.summary.changes_by_agent"
            :key="agentSummary.agent_id"
            class="bg-white border border-gray-200 rounded p-3 hover:border-blue-300 transition-colors"
          >
            <!-- Agent Header -->
            <div class="mb-2">
              <div class="text-sm font-semibold text-gray-900">{{ agentSummary.agent_name }}</div>
              <div class="text-xs text-gray-500">{{ agentSummary.total_changes }} değişiklik</div>
            </div>

            <!-- Top 5 Status Changes (Prioritized) -->
            <div class="mb-2 space-y-1">
              <div
                v-for="(change, index) in getPrioritizedStatusChanges(agentSummary.status_changes).slice(0, 5)"
                :key="index"
                class="flex items-center justify-between text-xs"
              >
                <div class="flex items-center space-x-1 flex-1 min-w-0">
                  <span class="text-gray-600 truncate">{{ change.old_status_name }}</span>
                  <span class="text-gray-400">→</span>
                  <span class="text-gray-900 font-medium truncate">{{ change.new_status_name }}</span>
                </div>
                <span class="ml-1 font-semibold text-blue-600 flex-shrink-0">{{ change.count }}</span>
              </div>
              <div v-if="agentSummary.status_changes.length === 0" class="text-xs text-gray-400 italic">
                Değişiklik yok
              </div>
            </div>

            <!-- Hepsini Gör Button -->
            <button
              @click="showAgentDetails(agentSummary.agent_id)"
              class="w-full px-3 py-1.5 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 focus:outline-none transition-colors"
            >
              Detay
            </button>
          </div>
        </div>
      </div>

      <!-- Filtered Agent Info & Clear Filter -->
      <div v-if="reportData && !loading && filteredAgentId" class="mb-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div>
          <div class="text-sm font-medium text-blue-900">
            Müşteri Temsilcisi Detayları
          </div>
          <div class="text-xs text-blue-700 mt-1">
            {{ getAgentName(filteredAgentId) }} için {{ filteredData.length }} kayıt gösteriliyor
          </div>
        </div>
        <button
          @click="clearFilter"
          class="px-4 py-2 bg-white text-blue-700 border border-blue-300 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        >
          ✕ Filtreyi Temizle
        </button>
      </div>

      <!-- Pagination Controls -->
      <div v-if="reportData && !loading && filteredData.length > 0" class="flex justify-between items-center mb-4">
        <div class="text-sm text-gray-700">
          Toplam {{ totalRows }} kayıt bulundu.
          {{ (currentPage - 1) * pageSize + 1 }} - {{ Math.min(currentPage * pageSize, totalRows) }} arası gösteriliyor.
        </div>
        <div class="flex gap-2">
          <button
            @click="previousPage"
            :disabled="currentPage === 1"
            class="px-3 py-1 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Önceki
          </button>
          <span class="px-3 py-1">
            Sayfa {{ currentPage }} / {{ totalPages }}
          </span>
          <button
            @click="nextPage"
            :disabled="currentPage === totalPages"
            class="px-3 py-1 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Sonraki
          </button>
        </div>
      </div>

      <!-- Report Table -->
      <div v-if="reportData && !loading && filteredAgentId" class="report-content">
        <div class="overflow-x-auto">
          <table class="min-w-full bg-white border border-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Saat
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Müşteri Temsilcisi
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Lead Bilgileri
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Eski Durum
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Yeni Durum
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="change in paginatedData"
                :key="change.journal_id"
                class="hover:bg-gray-50"
              >
                <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ formatTime(change.change_date) }}
                </td>
                <td class="px-4 py-4 whitespace-nowrap text-sm">
                  <div class="font-medium text-gray-900">{{ change.agent_name }}</div>
                  <div class="text-gray-500">{{ change.agent_username }}</div>
                </td>
                <td class="px-4 py-4 text-sm">
                  <div class="font-medium text-gray-900">
                    <a
                      :href="`/leadDetail/${change.lead_id}`"
                      target="_blank"
                      class="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {{ change.lead_name }}
                      <svg xmlns="http://www.w3.org/2000/svg" class="inline w-3 h-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>

                  <!-- Son 3 Not -->
                  <div v-if="change.last_notes && change.last_notes.length > 0" class="mt-2 border-t pt-2">
                    <div class="text-xs font-semibold text-gray-600 mb-1">Son Notlar:</div>
                    <div
                      v-for="(note, index) in change.last_notes"
                      :key="index"
                      class="text-xs bg-yellow-50 border-l-2 border-yellow-400 p-2 mb-1 rounded"
                    >
                      <div class="text-gray-700">{{ note.body }}</div>
                      <div class="text-gray-500 text-xs mt-1">
                        {{ note.note_author }} - {{ formatDateTime(note.note_date) }}
                      </div>
                    </div>
                  </div>
                </td>
                <td class="px-4 py-4 whitespace-nowrap">
                  <span class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                    {{ change.old_status_name || 'Bilinmiyor' }}
                  </span>
                </td>
                <td class="px-4 py-4 whitespace-nowrap">
                  <span class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                    {{ change.new_status_name || 'Bilinmiyor' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span class="ml-2 text-gray-600">Rapor yükleniyor...</span>
      </div>

      <!-- Empty State -->
      <div v-if="!reportData && !loading && hasSearched" class="text-center py-12">
        <div class="text-gray-500 text-lg mb-2">Veri bulunamadı</div>
        <div class="text-gray-400">Seçili tarihte lead durumu değişikliği bulunmamaktadır.</div>
      </div>

      <!-- Initial State -->
      <div v-if="!reportData && !loading && !hasSearched" class="text-center py-12">
 
        <div class="text-gray-400">Tarih ve müşteri temsilcisi seçerek raporu getirin.</div>
      </div>
    </div>
  </div>
</template>

<script>
import http from '../services/http'

export default {
  name: 'LeadStatusChangesReport',
  data() {
    const today = new Date().toISOString().split('T')[0]

    return {
      selectedDate: today,
      selectedAgent: '',
      reportData: null,
      loading: false,
      hasSearched: false,
      agentList: [],
      currentPage: 1,
      pageSize: 50,
      filteredAgentId: null
    }
  },
  mounted() {
    this.loadAgents()
  },
  computed: {
    allowedStatuses() {
      return [
        'Takip',
        'Yakın Takip',
        'Uzak Takip',
        'Yakın Zamanda Fonlama Yapacak',
        'Potansiyel müşteri adayı'
      ]
    },

    filteredData() {
      if (!this.reportData || !this.reportData.data) return []
      if (!this.filteredAgentId) return []

      // Hem agent'a göre hem de izin verilen durumlara göre filtrele
      return this.reportData.data.filter(item => {
        const isAgentMatch = item.agent_id == this.filteredAgentId
        const isAllowedStatus = this.allowedStatuses.some(s =>
          item.new_status_name && item.new_status_name.toLowerCase() === s.toLowerCase()
        )
        return isAgentMatch && isAllowedStatus
      })
    },

    totalRows() {
      return this.filteredData.length
    },

    totalPages() {
      return Math.ceil(this.totalRows / this.pageSize)
    },

    paginatedData() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.filteredData.slice(start, end)
    }
  },
  methods: {
    async loadAgents() {
      try {
        const response = await http.getUsers()
        if (response.data && response.data.users) {
          // Sadece aktif kullanıcıları göster
          this.agentList = response.data.users.filter(u => u.status === 1)
        }
      } catch (error) {
        console.error('Kullanıcılar yüklenemedi:', error)
        this.agentList = []
      }
    },

    async loadReport() {
      if (!this.selectedDate) {
        alert('Lütfen tarih seçin')
        return
      }

      this.loading = true
      this.hasSearched = true
      this.currentPage = 1
      this.filteredAgentId = null

      try {
        const response = await http.getLeadStatusChangesReport({
          date: this.selectedDate,
          agent_id: this.selectedAgent || null
        })

        if (response.data.success) {
          this.reportData = response.data
        } else {
          alert(response.data.message || 'Bir hata oluştu')
          this.reportData = null
        }
      } catch (error) {
        console.error('Report loading error:', error)
        alert('Rapor yüklenirken bir hata oluştu')
        this.reportData = null
      } finally {
        this.loading = false
      }
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    },

    formatTime(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleTimeString('tr-TR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },

    formatDateTime(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },

    showAgentDetails(agentId) {
      this.filteredAgentId = agentId
      this.currentPage = 1
      // Scroll to table
      this.$nextTick(() => {
        const tableElement = document.querySelector('.report-content')
        if (tableElement) {
          tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },

    clearFilter() {
      this.filteredAgentId = null
      this.currentPage = 1
    },

    getAgentName(agentId) {
      if (!this.reportData || !this.reportData.summary || !this.reportData.summary.changes_by_agent) {
        return ''
      }
      const agent = this.reportData.summary.changes_by_agent.find(a => a.agent_id == agentId)
      return agent ? agent.agent_name : ''
    },

    getPrioritizedStatusChanges(statusChanges) {
      if (!statusChanges || statusChanges.length === 0) return []

      // Sadece izin verilen durumları filtrele ve öncelik sırasına göre sırala
      return statusChanges
        .filter(change => {
          return this.allowedStatuses.some(s =>
            change.new_status_name && change.new_status_name.toLowerCase() === s.toLowerCase()
          )
        })
        .sort((a, b) => {
          const aIndex = this.allowedStatuses.findIndex(s =>
            a.new_status_name && a.new_status_name.toLowerCase() === s.toLowerCase()
          )
          const bIndex = this.allowedStatuses.findIndex(s =>
            b.new_status_name && b.new_status_name.toLowerCase() === s.toLowerCase()
          )
          // Öncelik sırasına göre, aynı ise count'a göre
          if (aIndex !== bIndex) return aIndex - bIndex
          return b.count - a.count
        })
    }
  }
}
</script>

<style scoped>
.lead-status-changes-report {
  padding: 1.5rem;
}
</style>
